import { v4 as uuidv4 } from 'uuid';
import photoUploadSessionStore from '@/services/photo-upload-session-store.js';

export default {
  data() {
    return {
      pendingResumeSummary: null,
      resumeAwaitingReselection: false,
      supportsFileSystemAccess: typeof window !== 'undefined' &&
        typeof window.showDirectoryPicker === 'function',
      preconfirmedBytes: 0,
    };
  },
  mounted() {
    window.addEventListener('pagehide', this.handlePageHide);
  },
  beforeDestroy() {
    this.cleanupPhotoUploadResume();
  },
  beforeUnmount() {
    this.cleanupPhotoUploadResume();
  },
  methods: {
    getFileRelativePath(file) {
      return file?.__runpixieRelativePath || file?.webkitRelativePath || file?.name || '';
    },
    buildFileSignature(file) {
      return [
        this.getFileRelativePath(file),
        file?.name || '',
        Number(file?.size || 0),
        Number(file?.lastModified || 0),
      ].join('|');
    },
    buildManifestFileSignature(fileMeta) {
      return [
        fileMeta?.relativePath || fileMeta?.name || '',
        fileMeta?.name || '',
        Number(fileMeta?.size || 0),
        Number(fileMeta?.lastModified || 0),
      ].join('|');
    },
    buildResumeFallbackSignature(fileLike) {
      return [
        fileLike?.name || '',
        Number(fileLike?.size || 0),
        Number(fileLike?.lastModified || 0),
      ].join('|');
    },
    decorateResumeFile(file, clientFileId = null, relativePath = null) {
      if (!file) {
        return file;
      }

      const define = (key, value) => {
        if (value === null || value === undefined || value === '') {
          return;
        }
        try {
          Object.defineProperty(file, key, {
            value,
            configurable: true,
          });
        } catch {
          try {
            file[key] = value;
          } catch {
            // File metadata is an enhancement only; fallback matching still works.
          }
        }
      };

      define('__runpixieClientFileId', clientFileId);
      define('__runpixieRelativePath', relativePath);
      return file;
    },
    plainSnapshot(value) {
      if (!value) {
        return value;
      }
      try {
        return JSON.parse(JSON.stringify(value));
      } catch {
        return null;
      }
    },
    createManifestFile(file, clientFileId) {
      return {
        clientFileId,
        name: file.name,
        size: Number(file.size || 0),
        lastModified: Number(file.lastModified || 0),
        type: file.type || null,
        relativePath: this.getFileRelativePath(file),
        confirmed: false,
        failed: false,
        confirmedAt: null,
      };
    },
    indexUploadManifest(manifest) {
      this._manifestFilesById = new Map(
        (manifest?.files || []).map((file) => [file.clientFileId, file])
      );
    },
    async createFreshUploadManifest(files) {
      const manifestFiles = files.map((file) => {
        const clientFileId = file.__runpixieClientFileId || uuidv4();
        this.decorateResumeFile(file, clientFileId, this.getFileRelativePath(file));
        return this.createManifestFile(file, clientFileId);
      });

      const manifest = {
        version: 1,
        uploadId: this.uuidValue,
        telemetrySessionId: this.telemetrySessionId,
        userId: this.user?.id ?? null,
        wpRunId: this.selectedEvent?.id ?? null,
        status: 'active',
        createdAt: Date.now(),
        updatedAt: Date.now(),
        totalFiles: manifestFiles.length,
        totalBytes: manifestFiles.reduce((sum, file) => sum + file.size, 0),
        selectedEvent: this.plainSnapshot(this.selectedEvent),
        selectedCountry: this.plainSnapshot(this.selectedCountry),
        selectedCity: this.plainSnapshot(this.selectedCity),
        directoryHandle: this._selectedDirectoryHandle || null,
        files: manifestFiles,
      };

      this._uploadManifest = manifest;
      this.indexUploadManifest(manifest);
      await this.persistCurrentUploadManifest();
      return manifest;
    },
    async persistCurrentUploadManifest(status = null) {
      if (!this._uploadManifest) {
        return;
      }

      if (status) {
        this._uploadManifest.status = status;
      }
      this._uploadManifest.updatedAt = Date.now();
      this._uploadManifest.telemetrySessionId = this.telemetrySessionId ||
        this._uploadManifest.telemetrySessionId;
      this._uploadManifest.selectedEvent = this.plainSnapshot(this.selectedEvent) ||
        this._uploadManifest.selectedEvent;
      this._uploadManifest.selectedCountry = this.plainSnapshot(this.selectedCountry) ||
        this._uploadManifest.selectedCountry;
      this._uploadManifest.selectedCity = this.plainSnapshot(this.selectedCity) ||
        this._uploadManifest.selectedCity;
      if (this._selectedDirectoryHandle) {
        this._uploadManifest.directoryHandle = this._selectedDirectoryHandle;
      }

      try {
        const saved = await photoUploadSessionStore.saveSession(this._uploadManifest);
        if (saved && !saved.directoryHandle && this._uploadManifest.directoryHandle) {
          this._uploadManifest.directoryHandle = null;
        }
      } catch (error) {
        console.warn('[photo-upload] Could not persist upload manifest', error);
      }
    },
    scheduleUploadManifestPersist(status = null) {
      if (!this._uploadManifest) {
        return;
      }
      if (status) {
        this._uploadManifest.status = status;
      }
      if (this._manifestPersistTimer) {
        return;
      }
      this._manifestPersistTimer = setTimeout(() => {
        this._manifestPersistTimer = null;
        this.persistCurrentUploadManifest();
      }, 2000);
    },
    markManifestFileConfirmed(clientFileId) {
      if (!this._uploadManifest || !clientFileId) {
        return;
      }
      const file = this._manifestFilesById?.get(clientFileId);
      if (!file || file.confirmed) {
        return;
      }
      file.confirmed = true;
      file.failed = false;
      file.confirmedAt = Date.now();
      this.scheduleUploadManifestPersist('active');
    },
    markManifestFileFailed(clientFileId) {
      if (!this._uploadManifest || !clientFileId) {
        return;
      }
      const file = this._manifestFilesById?.get(clientFileId);
      if (!file || file.confirmed) {
        return;
      }
      file.failed = true;
      this.scheduleUploadManifestPersist('failed');
    },
    getManifestConfirmedCount(manifest = this._uploadManifest) {
      return manifest?.files?.filter((file) => file.confirmed).length || 0;
    },
    getManifestConfirmedBytes(manifest = this._uploadManifest) {
      return manifest?.files?.reduce(
        (sum, file) => sum + (file.confirmed ? Number(file.size || 0) : 0),
        0
      ) || 0;
    },
    getManifestRemainingFiles(manifest = this._uploadManifest) {
      return manifest?.files?.filter((file) => !file.confirmed) || [];
    },
    restoreUploadContextFromManifest(manifest) {
      this.uuidValue = manifest.uploadId;
      this.telemetrySessionId = manifest.telemetrySessionId || uuidv4();
      this.selectedEvent = manifest.selectedEvent || this.selectedEvent;
      this.selectedCountry = manifest.selectedCountry || this.selectedCountry;
      this.selectedCity = manifest.selectedCity || this.selectedCity;
      this.originalImageCount = Number(manifest.totalFiles || manifest.files?.length || 0);
      this._uploadManifest = manifest;
      this.indexUploadManifest(manifest);
      this._selectedDirectoryHandle = manifest.directoryHandle || null;
    },
    exposeCurrentManifestAsPending(awaitingReselection = true) {
      const manifest = this._uploadManifest;
      if (!manifest) {
        return;
      }
      const remainingFiles = this.getManifestRemainingFiles(manifest).length;
      this._pendingResumeManifest = manifest;
      this.pendingResumeSummary = {
        uploadId: manifest.uploadId,
        totalFiles: Number(manifest.totalFiles || manifest.files?.length || 0),
        confirmedFiles: this.getManifestConfirmedCount(manifest),
        remainingFiles,
        hasDirectoryHandle: Boolean(manifest.directoryHandle),
      };
      this.resumeAwaitingReselection = awaitingReselection;
    },
    async detectPendingUploadManifest() {
      if (!this.user?.id || this.isUploadRunning || this.isUploadStarting) {
        return;
      }
      const requestedUserId = this.user.id;
      try {
        const manifest = await photoUploadSessionStore.getLatestIncompleteSession(requestedUserId);
        if (
          this.isUploadRunning ||
          this.isUploadStarting ||
          String(this.user?.id ?? '') !== String(requestedUserId)
        ) {
          return;
        }
        if (!manifest) {
          return;
        }
        const remainingFiles = this.getManifestRemainingFiles(manifest).length;
        if (!remainingFiles) {
          await photoUploadSessionStore.deleteSession(manifest.uploadId);
          return;
        }
        this._pendingResumeManifest = manifest;
        this.pendingResumeSummary = {
          uploadId: manifest.uploadId,
          totalFiles: Number(manifest.totalFiles || manifest.files?.length || 0),
          confirmedFiles: this.getManifestConfirmedCount(manifest),
          remainingFiles,
          hasDirectoryHandle: Boolean(manifest.directoryHandle),
        };
        this.step = 1;
      } catch (error) {
        console.warn('[photo-upload] Could not read pending upload manifest', error);
      }
    },
    async discardPendingUpload() {
      const manifest = this._pendingResumeManifest;
      if (manifest?.uploadId) {
        try {
          await photoUploadSessionStore.deleteSession(manifest.uploadId);
        } catch (error) {
          console.warn('[photo-upload] Could not delete upload manifest', error);
        }
      }
      if (this._uploadManifest?.uploadId === manifest?.uploadId) {
        this._uploadManifest = null;
        this._manifestFilesById = null;
      }
      this._pendingResumeManifest = null;
      this.pendingResumeSummary = null;
      this.resumeAwaitingReselection = false;
      this._selectedDirectoryHandle = null;
    },
    async ensureDirectoryReadPermission(handle) {
      if (!handle) {
        return false;
      }
      try {
        if (typeof handle.queryPermission === 'function') {
          const state = await handle.queryPermission({ mode: 'read' });
          if (state === 'granted') {
            return true;
          }
        }
        if (typeof handle.requestPermission === 'function') {
          return (await handle.requestPermission({ mode: 'read' })) === 'granted';
        }
        return true;
      } catch {
        return false;
      }
    },
    isSupportedPhotoFile(file) {
      const extension = this.getFileExtension(file?.name || '');
      return ['jpg', 'jpeg', 'png', 'bmp'].includes(extension);
    },
    async collectFilesFromDirectory(directoryHandle, prefix = '') {
      const files = [];
      for await (const [name, entry] of directoryHandle.entries()) {
        const relativePath = prefix ? `${prefix}/${name}` : name;
        if (entry.kind === 'file') {
          const file = await entry.getFile();
          if (this.isSupportedPhotoFile(file)) {
            this.decorateResumeFile(file, null, relativePath);
            files.push(file);
          }
          continue;
        }
        if (entry.kind === 'directory') {
          const nested = await this.collectFilesFromDirectory(entry, relativePath);
          files.push(...nested);
        }
      }
      return files;
    },
    async selectResumableFolder() {
      if (!this.supportsFileSystemAccess || this.isUploadRunning) {
        return;
      }
      try {
        const handle = await window.showDirectoryPicker({
          id: 'runpixie-photo-upload',
          mode: 'read',
        });
        const files = await this.collectFilesFromDirectory(handle);
        if (!files.length) {
          this.$q.notify({
            icon: 'warning',
            color: 'warning',
            message: this.$t('addMedia.resume.noSupportedFiles'),
            position: 'top-right',
          });
          return;
        }
        this._selectedDirectoryHandle = handle;

        // During recovery the photographer may reselect the original folder,
        // which contains both already-confirmed and still-pending photos.
        // Keep only files that belong to the remaining part of the saved session
        // so the existing `remainingFiles` UI guard can become valid again.
        const manifest = this._pendingResumeManifest || this._uploadManifest;
        const isRecoverySelection = Boolean(
          manifest && this.pendingResumeSummary && this.resumeAwaitingReselection
        );
        let selectedFiles = files;
        if (isRecoverySelection) {
          const { matched, required } = this.matchResumeFiles(manifest, files);
          selectedFiles = matched;

          if (matched.length !== required) {
            this.$q.notify({
              icon: 'warning',
              color: 'warning',
              timeout: 7000,
              message: this.$t('addMedia.resume.filesMismatch', {
                matched: matched.length,
                required,
              }),
              position: 'top-right',
            });
          }
        }

        this.selectedImage = selectedFiles;
        this.maybeShowLargeUploadNotice(selectedFiles.length);
        if (this.$refs.FilePickerRef) {
          await this.$refs.FilePickerRef.addFromParent(selectedFiles);
        }
      } catch (error) {
        if (error?.name !== 'AbortError') {
          console.warn('[photo-upload] Directory selection failed', error);
          this.$q.notify({
            icon: 'warning',
            color: 'negative',
            message: this.$t('addMedia.resume.folderError'),
            position: 'top-right',
          });
        }
      }
    },
    matchResumeFiles(manifest, selectedFiles) {
      const remaining = this.getManifestRemainingFiles(manifest);
      const bySignature = new Map();
      const byFallbackSignature = new Map();
      const consumedIds = new Set();

      remaining.forEach((fileMeta) => {
        const signature = this.buildManifestFileSignature(fileMeta);
        if (!bySignature.has(signature)) {
          bySignature.set(signature, []);
        }
        bySignature.get(signature).push(fileMeta);

        // A folder reselected through another browser API can expose a different
        // relativePath (for example with or without the root directory name).
        // Keep a path-independent fallback for the same physical file metadata.
        const fallbackSignature = this.buildResumeFallbackSignature(fileMeta);
        if (!byFallbackSignature.has(fallbackSignature)) {
          byFallbackSignature.set(fallbackSignature, []);
        }
        byFallbackSignature.get(fallbackSignature).push(fileMeta);
      });

      const matched = [];
      const unmatchedSelected = [];

      // Prefer exact relative-path matching first.
      selectedFiles.forEach((file) => {
        const signature = this.buildFileSignature(file);
        const candidates = bySignature.get(signature);
        const fileMeta = candidates?.find((candidate) => !consumedIds.has(candidate.clientFileId));
        if (!fileMeta) {
          unmatchedSelected.push(file);
          return;
        }

        consumedIds.add(fileMeta.clientFileId);
        this.decorateResumeFile(file, fileMeta.clientFileId, fileMeta.relativePath);
        matched.push(file);
      });

      // Fall back to name + size + lastModified when only the directory prefix
      // differs between the saved manifest and the newly selected folder.
      unmatchedSelected.forEach((file) => {
        const fallbackSignature = this.buildResumeFallbackSignature(file);
        const candidates = byFallbackSignature.get(fallbackSignature);
        const fileMeta = candidates?.find((candidate) => !consumedIds.has(candidate.clientFileId));
        if (!fileMeta) {
          return;
        }

        consumedIds.add(fileMeta.clientFileId);
        this.decorateResumeFile(file, fileMeta.clientFileId, fileMeta.relativePath);
        matched.push(file);
      });

      return {
        matched,
        required: remaining.length,
      };
    },
    async resumeWithFiles(files) {
      const manifest = this._pendingResumeManifest || this._uploadManifest;
      if (!manifest || this.isUploadRunning) {
        return;
      }

      const { matched, required } = this.matchResumeFiles(manifest, files);
      if (matched.length !== required) {
        this.resumeAwaitingReselection = true;
        this.$q.notify({
          icon: 'warning',
          color: 'warning',
          timeout: 7000,
          message: this.$t('addMedia.resume.filesMismatch', {
            matched: matched.length,
            required,
          }),
          position: 'top-right',
        });
        return;
      }

      this.restoreUploadContextFromManifest(manifest);
      this.selectedImage = matched;
      const confirmedBeforeResume = this.getManifestConfirmedCount(manifest);
      this._pendingResumeManifest = null;
      this.pendingResumeSummary = null;
      this.resumeAwaitingReselection = false;
      this.$q.notify({
        icon: 'play_arrow',
        color: 'positive',
        timeout: 4000,
        message: this.$t('addMedia.guide.resumedNotice', {
          confirmed: confirmedBeforeResume,
          remaining: matched.length,
        }),
        position: 'top-right',
      });
      await this.startPhotoUpload(matched, true, manifest);
    },
    async resumePendingUpload() {
      const manifest = this._pendingResumeManifest;
      if (!manifest || this.isUploadRunning) {
        return;
      }

      this.restoreUploadContextFromManifest(manifest);
      if (manifest.directoryHandle && this.supportsFileSystemAccess) {
        const allowed = await this.ensureDirectoryReadPermission(manifest.directoryHandle);
        if (allowed) {
          try {
            const files = await this.collectFilesFromDirectory(manifest.directoryHandle);
            await this.resumeWithFiles(files);
            return;
          } catch (error) {
            console.warn('[photo-upload] Could not restore files from directory handle', error);
          }
        }
      }

      this.resumeAwaitingReselection = true;
      this.step = 1;
      this.$q.notify({
        icon: 'info',
        color: 'info',
        timeout: 7000,
        message: this.$t('addMedia.resume.reselectInstruction'),
        position: 'top-right',
      });
    },
    async resumeFromSelectedFiles() {
      await this.resumeWithFiles([...this.selectedImage]);
    },
    handlePageHide() {
      if (this._uploadManifest) {
        this.persistCurrentUploadManifest();
      }
    },
    cleanupPhotoUploadResume() {
      if (this._resumeCleanupDone) {
        return;
      }
      this._resumeCleanupDone = true;
      if (this._manifestPersistTimer) {
        clearTimeout(this._manifestPersistTimer);
        this._manifestPersistTimer = null;
      }
      this.persistCurrentUploadManifest();
      window.removeEventListener('pagehide', this.handlePageHide);
    },
  },
};
